import { useState, useCallback, useMemo } from 'react'
import { API } from 'core/requests'
import SnackbarUtils from 'core/SnackbarConfiguration'
import { getRecognitionId } from '../utils/recognitionUtils'

interface UseDeleteRecognitionProps {
  companyId: number
  onDeleteSuccess: () => void
  selectedReportData: any
}

export const useDeleteRecognition = ({
  companyId,
  onDeleteSuccess,
  selectedReportData,
}: UseDeleteRecognitionProps) => {
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  const [selectedRecognitionToDelete, setSelectedRecognitionToDelete] = useState<any>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [deleteConfirmationText, setDeleteConfirmationText] = useState('')
  const [deletedRecognitionIds, setDeletedRecognitionIds] = useState<Set<any>>(new Set())

  // Get all users with the same recognition_id
  const relatedRecognitions = useMemo(() => {
    if (!selectedRecognitionToDelete) return []

    const recognitionId = getRecognitionId(selectedRecognitionToDelete)
    const dataSnapshot = selectedReportData?.data || []

    return dataSnapshot.filter((row: any) => {
      return getRecognitionId(row) === recognitionId
    })
  }, [selectedRecognitionToDelete, selectedReportData])

  // Memoize the button disabled state
  const isRemoveButtonDisabled = useMemo(() => {
    return isDeleting || deleteConfirmationText.toLowerCase() !== 'remove'
  }, [isDeleting, deleteConfirmationText])

  const handleDeleteRecognitionClick = useCallback((row: any) => {
    setSelectedRecognitionToDelete(row)
    setDeleteConfirmationText('')
    setConfirmDialogOpen(true)
  }, [])

  const handleConfirmDelete = useCallback(async () => {
    if (!selectedRecognitionToDelete) return

    const recognitionId = getRecognitionId(selectedRecognitionToDelete)

    if (!recognitionId) {
      SnackbarUtils.error('Unable to find recognition ID')
      console.error('Recognition row data:', selectedRecognitionToDelete)
      return
    }

    setIsDeleting(true)
    try {
      await API.delete('sparcks', recognitionId, {
        queryParams: { company_id: companyId }
      })

      // Immediately remove the recognition from UI (optimistic update)
      setDeletedRecognitionIds(prev => new Set(prev).add(recognitionId))

      SnackbarUtils.success('Recognition removed successfully')
      setConfirmDialogOpen(false)
      setSelectedRecognitionToDelete(null)
      setDeleteConfirmationText('')

      // Fetch fresh data from server to ensure consistency
      onDeleteSuccess()
    } catch (error: any) {
      console.error('Error deleting recognition:', error)
      SnackbarUtils.error(
        error?.response?.data?.message || 'Failed to remove recognition'
      )
    } finally {
      setIsDeleting(false)
    }
  }, [selectedRecognitionToDelete, companyId, onDeleteSuccess])

  const handleCancelDelete = useCallback(() => {
    setConfirmDialogOpen(false)
    setSelectedRecognitionToDelete(null)
    setDeleteConfirmationText('')
  }, [])

  const resetDeletedIds = useCallback(() => {
    setDeletedRecognitionIds(new Set())
  }, [])

  return {
    // State
    confirmDialogOpen,
    selectedRecognitionToDelete,
    isDeleting,
    deleteConfirmationText,
    deletedRecognitionIds,
    relatedRecognitions,
    isRemoveButtonDisabled,

    // Handlers
    handleDeleteRecognitionClick,
    handleConfirmDelete,
    handleCancelDelete,
    setDeleteConfirmationText,
    resetDeletedIds,
  }
}
